import { themeBoxShadow, themeColor } from '@config/theme';
import { mdiArrowLeft, mdiClose, mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import {
	Box,
	IconButton,
	InputAdornment,
	TextField,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatStore } from '../store';

// Debounce utility
const debounce = (func: Function, delay: number) => {
	let timeoutId: NodeJS.Timeout;
	return function (this: any, ...args: any[]) {
		clearTimeout(timeoutId);
		timeoutId = setTimeout(() => func.apply(this, args), delay);
	};
};

interface MessageSearchHeaderProps {
	onSearch: (query: string) => void;
	onClose: () => void;
	resultCount?: number;
	onSearchInputRef?: (ref: HTMLElement | null) => void;
}

export const MessageSearchHeader: React.FC<MessageSearchHeaderProps> = ({
	onSearch,
	onClose,
	resultCount,
	onSearchInputRef,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation();
	const { messageSearchQuery, setMessageSearchQuery } = useChatStore();
	const [localQuery, setLocalQuery] = useState(messageSearchQuery);
	const inputRef = useRef<HTMLInputElement>(null);

	// Debounced search function
	const debouncedSearch = useCallback(
		debounce((query: string) => {
			if (query.trim().length >= 3) {
				onSearch(query.trim());
			}
		}, 500),
		[onSearch]
	);

	// Handle input change
	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setLocalQuery(value);
		setMessageSearchQuery(value);
		
		// Trigger search if query is long enough
		if (value.trim().length >= 3) {
			debouncedSearch(value);
		}
	};

	// Handle close
	const handleClose = () => {
		setLocalQuery('');
		setMessageSearchQuery('');
		onClose();
	};

	// Focus input on mount
	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, []);

	return (
		<Box
			boxShadow={themeBoxShadow.main}
			minHeight={'72px'}
			width={'100%'}
			display="flex"
			alignItems="center"
			bgcolor={themeColor.veryLightGray}
			paddingX={2}
		>
			<Box
				display="flex"
				alignItems="center"
				width="100%"
				gap={1}
			>
				{/* Back button for mobile, close button for desktop */}
				<IconButton 
					onClick={handleClose} 
					aria-label={isMobile ? 'back' : 'close'}
					sx={{ flexShrink: 0 }}
				>
					<Icon 
						path={isMobile ? mdiArrowLeft : mdiClose} 
						size={1} 
						color={themeColor.mediumGray} 
					/>
				</IconButton>

				{/* Search input */}
				<TextField
					ref={(el) => {
						// @ts-ignore - inputRef is for focus management
						inputRef.current = el?.querySelector('input') || null;
						// Pass the TextField element for popover positioning
						if (onSearchInputRef) {
							onSearchInputRef(el);
						}
					}}
					placeholder={t('chats.searchMessagesPlaceholder', 'Search messages...')}
					variant="outlined"
					size="small"
					value={localQuery}
					onChange={handleInputChange}
					sx={{
						flex: 1,
						backgroundColor: theme.palette.background.paper,
						'& .MuiOutlinedInput-root': {
							backgroundColor: theme.palette.background.paper,
							fontSize: '14px',
							'&:hover': {
								backgroundColor: theme.palette.background.paper,
							},
							'&.Mui-focused': {
								backgroundColor: theme.palette.background.paper,
							},
						},
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: themeColor.lightGray,
						},
						'& .MuiOutlinedInput-input': {
							padding: '8px 12px',
						},
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Icon 
									path={mdiMagnify} 
									size={1} 
									color={themeColor.mediumGray} 
								/>
							</InputAdornment>
						),
					}}
				/>

				{/* Show result count when available */}
				{resultCount !== undefined && resultCount > 0 && (
					<Box
						sx={{
							fontSize: '12px',
							color: themeColor.mediumGray,
							whiteSpace: 'nowrap',
							px: 1,
						}}
					>
						{resultCount} result{resultCount === 1 ? '' : 's'}
					</Box>
				)}
			</Box>
		</Box>
	);
}; 